import { useCurrentUser } from '@mp/hooks';
import React, { useEffect, useRef } from 'react';
import calendlyWidget from '@mp/utils/calendlyWidget';
import { config } from '@mp/config';
import { useCalendlySchedulingMutations } from './mutations';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidgets: () => void;
    };
  }
}

type CalendlyParams = {
  name?: string;
  email?: string;
  a1?: string;
};

const schedulingCompleted = (e: MessageEvent) =>
  e.origin === 'https://calendly.com' && e.data.event === 'calendly.event_scheduled';

export const PhoneScreeningSchedulerForm = () => {
  const { currentUser } = useCurrentUser();
  const { schedulingCompletedMutation } = useCalendlySchedulingMutations();
  const calendlyURL = config.calendlyAssignmentURL;
  const processedEventRef = useRef<string | null>(null);

  const buildCalendlyURL = (): string => {
    if (!currentUser) {
      return calendlyURL;
    }

    const { first_name, last_name, email, phone_number } = currentUser;
    const fullName = [first_name, last_name].filter(Boolean).join(' ').trim();
    const calendlyParams: CalendlyParams = {};

    calendlyParams.name = fullName;
    calendlyParams.email = email;
    calendlyParams.a1 = phone_number;

    const params = new URLSearchParams();

    Object.entries(calendlyParams).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    const queryString = params.toString();
    return queryString ? `${calendlyURL}?${queryString}` : calendlyURL;
  };

  useEffect(() => {
    calendlyWidget();

    if (window.Calendly?.initInlineWidgets) {
      window.Calendly.initInlineWidgets();
    }
  }, []);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (schedulingCompleted(e)) {
        if (processedEventRef.current === e.data.payload.event.uri) {
          return;
        }
        processedEventRef.current = e.data.payload.event.uri;
        const payload = {
          professional_screening: {
            calendly_scheduled_event_uri: e.data.payload.event.uri,
            calendly_scheduled_event_invitee_uri: e.data.payload.invitee.uri,
            reason: 're_screening',
            screening_type: 'phone',
          },
        };
        schedulingCompletedMutation(payload);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [schedulingCompletedMutation]);

  return (
    <div className="calendly-inline-widget" data-url={buildCalendlyURL()} style={{ height: 650 }} />
  );
};
